/**
 * ChaptersProvider
 * Провайдер для работы с главами и сценами книги
 */

import { createContext, useEffect, useState, ReactNode, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Chapter,
  CreateChapterInput,
  UpdateChapterInput,
  ChapterError,
} from '@/types/chapter';
import { Scene, CreateSceneInput, UpdateSceneInput, SceneError } from '@/types/scene';
import { useBooks } from '@/books/useBooks';
import { useAuth } from '@/auth/useAuth';

interface ChaptersContextType {
  chapters: Chapter[];
  scenes: Scene[];
  currentChapter: Chapter | null;
  currentScene: Scene | null;
  chaptersLoading: boolean;
  scenesLoading: boolean;
  chaptersError: ChapterError | null;
  scenesError: SceneError | null;
  createChapter: (input: CreateChapterInput) => Promise<Chapter | null>;
  createScene: (input: CreateSceneInput) => Promise<Scene | null>;
  selectChapter: (chapterId: string) => void;
  selectScene: (sceneId: string) => void;
  saveChapter: (chapterId: string, updates: UpdateChapterInput) => Promise<void>;
  deleteChapter: (chapterId: string) => Promise<void>;
  saveScene: (sceneId: string, updates: UpdateSceneInput) => Promise<void>;
  loadChapters: () => Promise<void>;
}

export const ChaptersContext = createContext<ChaptersContextType | undefined>(undefined);

/**
 * Which chapter stays selected after the chapter list (re)loads.
 *
 * Keep the CURRENT selection if it still exists, otherwise fall back to the first chapter. The
 * selection is re-read from the fresh list so a stale object (or a chapter deleted elsewhere) never
 * survives. Exported for unit tests.
 *
 * This must be called from inside a setState updater, not against a ref: the ref used previously was
 * written in an effect — after render — so a load finishing in that window saw no selection and
 * reset the reader to chapter 1, including right after a deep link had chosen another one (T-D02).
 */
export function resolveCurrentChapter(
  previous: Chapter | null,
  loadedChapters: Chapter[],
  rememberedChapterId?: string | null,
): Chapter | null {
  const stillThere = previous ? loadedChapters.find((chapter) => chapter.id === previous.id) ?? null : null;
  const remembered = rememberedChapterId
    ? loadedChapters.find((chapter) => chapter.id === rememberedChapterId) ?? null
    : null;

  // Precedence is NOT interchangeable — this is the T-D01 lesson applied up front. The profile write
  // is async, so letting `remembered` win would let a stale read replace the chapter the reader just
  // opened. On a cold start `previous` is null, so the remembered chapter still decides.
  return stillThere ?? remembered ?? loadedChapters[0] ?? null;
}

/**
 * Pure reselect decision used when the active chapter is deleted: from the pre-delete,
 * order-sorted chapter list, prefer the NEXT chapter, else the PREVIOUS, else null when no
 * chapters remain. Returns null if the deleted id is not in the list. Exported for unit tests.
 */
export function resolveChapterAfterDelete(
  chapters: Chapter[],
  deletedChapterId: string,
): Chapter | null {
  const index = chapters.findIndex((chapter) => chapter.id === deletedChapterId);

  if (index === -1) {
    return null;
  }

  const remaining = chapters.filter((chapter) => chapter.id !== deletedChapterId);

  return remaining[index] ?? remaining[index - 1] ?? null;
}

interface ChaptersProviderProps {
  children: ReactNode;
}

export function ChaptersProvider({ children }: ChaptersProviderProps) {
  const { currentBook } = useBooks();
  const { profile, updateActiveChapterId } = useAuth();
  const rememberedChapterId = profile?.active_chapter_id ?? null;
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [currentScene, setCurrentScene] = useState<Scene | null>(null);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [scenesLoading, setScenesLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState<ChapterError | null>(null);
  const [scenesError, setScenesError] = useState<SceneError | null>(null);

  // CR-3 (v2-only cutover): FE no longer persists the chapter manuscript to
  // chapters.description. The legacy debounced manuscript autosave (flush +
  // classic save helpers) was removed; Book Editor v2 (RPC) is the only durable
  // manuscript write path. chapters.description stays readable (classic view +
  // v2 fallback) until CR-5/CR-8 retire it.


  // T-J01. `loadChapters` is re-created whenever `currentBook`'s IDENTITY or `rememberedChapterId`
  // changes — and both change more often than the data does: the book object is not identity-stable
  // (B4-7), and the remembered id is rewritten by this tab's own `updateActiveChapterId` after its
  // round-trip. Each re-creation re-runs the effect below and fires ANOTHER chapter fetch, with no
  // cancellation and no ordering guarantee. Two in flight, answered out of order, and the LATE one
  // resolves last: its (older) list overwrites the selection with `loadedChapters[0]` when the
  // chapter the reader asked for is not in it.
  //
  // That is the deep-link fault. Measured: the failing spec passes 24/24 alone at four workers, and
  // fails 3/21 with nothing changed except an external process rewriting `active_chapter_id` every
  // 120 ms — a CHANGING value spawns loads, a stable conflicting one does not, which is why a fixed
  // remembered chapter never reproduced it.
  //
  // Guard: only the newest request is allowed to write state. Not `AbortController` — the response
  // is still useful to nobody, and a sequence number keeps the failure mode to "ignored" rather
  // than "rejected promise to handle".
  const chaptersRequestRef = useRef(0);

  // Загрузка глав текущей книги
  const loadChapters = useCallback(async () => {
    if (!currentBook) {
      // Still bump: a book-cleared reset must supersede any fetch already in flight, or that fetch
      // would repopulate the list for a book that is no longer open.
      chaptersRequestRef.current += 1;
      setChapters([]);
      setCurrentChapter(null);
      setScenes([]);
      setCurrentScene(null);
      return;
    }

    const requestId = (chaptersRequestRef.current += 1);
    const isStale = () => requestId !== chaptersRequestRef.current;

    setChaptersLoading(true);
    setChaptersError(null);

    try {
      const { data, error } = await supabase
        .from('chapters')
        .select('*')
        .eq('book_id', currentBook.id)
        .order('order_index', { ascending: true });

      // A superseded response must not touch state — not the list, not the selection, not the
      // error. It is answering a question nobody is asking any more.
      if (isStale()) {
        return;
      }

      if (error) {
        console.error('Chapters load error:', error);
        setChaptersError({ message: 'Не удалось загрузить главы', code: error.code });
        setChapters([]);
      } else {
        const loadedChapters = data || [];
        setChapters(loadedChapters);

        // Which chapter stays selected — resolved from the CURRENT state inside the updater, never
        // from a ref. loadChapters does not re-create on chapter change (deps=[currentBook]), so it
        // used to read a latest-ref instead; that ref is written in an effect, i.e. AFTER render, so
        // a load finishing in that window saw `null`, decided nothing was selected and picked
        // chapter 1 — overwriting a chapter that had just been selected (T-D02). The functional
        // updater closes that window: React hands us the live value.
        setCurrentChapter((previous) => resolveCurrentChapter(previous, loadedChapters, rememberedChapterId));
      }
    } catch (err) {
      if (isStale()) {
        return;
      }
      console.error('Chapters load exception:', err);
      setChaptersError({ message: 'Произошла ошибка при загрузке глав' });
      setChapters([]);
    } finally {
      // Only the newest request owns the spinner: an older one finishing later would otherwise
      // clear it while the current fetch is still running.
      if (!isStale()) {
        setChaptersLoading(false);
      }
    }
  }, [currentBook, rememberedChapterId]);

  // Загрузка сцен выбранной главы
  const loadScenes = useCallback(async (chapterId: string) => {
    setScenesLoading(true);
    setScenesError(null);

    try {
      const { data, error } = await supabase
        .from('scenes')
        .select('*')
        .eq('chapter_id', chapterId)
        .order('order_index', { ascending: true });

      if (error) {
        console.error('Scenes load error:', error);
        setScenesError({ message: 'Не удалось загрузить сцены', code: error.code });
        setScenes([]);
      } else {
        setScenes(data || []);
        
        // Если есть сцены и нет выбранной - выбираем первую
        if (data && data.length > 0 && !currentScene) {
          setCurrentScene(data[0]);
        }
      }
    } catch (err) {
      console.error('Scenes load exception:', err);
      setScenesError({ message: 'Произошла ошибка при загрузке сцен' });
      setScenes([]);
    } finally {
      setScenesLoading(false);
    }
  }, [currentScene]);

  // Создание новой главы
  const createChapter = useCallback(async (input: CreateChapterInput): Promise<Chapter | null> => {
    if (!currentBook) {
      setChaptersError({ message: 'Книга не выбрана' });
      return null;
    }

    setChaptersError(null);

    try {
      // Получаем максимальный order_index
      const maxOrderIndex = chapters.length > 0 
        ? Math.max(...chapters.map(c => c.order_index))
        : -1;

      const newChapter = {
        book_id: currentBook.id,
        title: input.title,
        order_index: maxOrderIndex + 1,
        chapter_summary: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('chapters')
        .insert([newChapter])
        .select()
        .single();

      if (error) {
        console.error('Chapter create error:', error);
        setChaptersError({ message: 'Не удалось создать главу', code: error.code });
        return null;
      }

      setChapters(prev => [...prev, data]);
      setCurrentChapter(data);
      
      return data;
    } catch (err) {
      console.error('Chapter create exception:', err);
      setChaptersError({ message: 'Произошла ошибка при создании главы' });
      return null;
    }
  }, [currentBook, chapters]);

  // Создание новой сцены
  const createScene = useCallback(async (input: CreateSceneInput): Promise<Scene | null> => {
    if (!currentChapter) {
      setScenesError({ message: 'Глава не выбрана' });
      return null;
    }

    setScenesError(null);

    try {
      // Получаем максимальный order_index
      const maxOrderIndex = scenes.length > 0 
        ? Math.max(...scenes.map(s => s.order_index))
        : -1;

      const newScene = {
        chapter_id: currentChapter.id,
        title: input.title,
        content: input.content || null,
        notes: input.notes || null,
        goal: input.goal || null,
        order_index: maxOrderIndex + 1,
        scene_summary: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('scenes')
        .insert([newScene])
        .select()
        .single();

      if (error) {
        console.error('Scene create error:', error);
        setScenesError({ message: 'Не удалось создать сцену', code: error.code });
        return null;
      }

      setScenes(prev => [...prev, data]);
      setCurrentScene(data);
      
      return data;
    } catch (err) {
      console.error('Scene create exception:', err);
      setScenesError({ message: 'Произошла ошибка при создании сцены' });
      return null;
    }
  }, [currentChapter, scenes]);

  // Выбор главы
  const selectChapter = useCallback((chapterId: string) => {
    const chapter = chapters.find(c => c.id === chapterId);
    if (chapter) {
      setCurrentChapter(chapter);
      setCurrentScene(null);
      // Remember it so a reload comes back here instead of chapter 1. Fire-and-forget: the write is
      // a convenience and must never block or break selection if it fails.
      void updateActiveChapterId(chapter.id);
    }
  }, [chapters, updateActiveChapterId]);

  // Выбор сцены
  const selectScene = useCallback((sceneId: string) => {
    const scene = scenes.find(s => s.id === sceneId);
    if (scene) {
      setCurrentScene(scene);
    }
  }, [scenes]);

  // Сохранение изменений главы
  const saveChapter = useCallback(async (chapterId: string, updates: UpdateChapterInput) => {
    try {
      const { data, error } = await supabase
        .from('chapters')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', chapterId)
        .select()
        .single();

      if (error) {
        console.error('Chapter save error:', error);
        setChaptersError({ message: 'Не удалось сохранить главу', code: error.code });
        return;
      }

      setChapters(prev => prev.map(c => c.id === chapterId ? data : c));
      
      if (currentChapter?.id === chapterId) {
        setCurrentChapter(data);
      }
    } catch (err) {
      console.error('Chapter save exception:', err);
      setChaptersError({ message: 'Произошла ошибка при сохранении главы' });
    }
  }, [currentChapter]);

  // Удаление главы — ТОЛЬКО строка таблицы + локальное состояние + переселект.
  // scenes/ai_threads удаляются каскадом (FK ON DELETE CASCADE) — вручную не трогаем.
  // Синхронизация v2 document_json (save_book_editor_v2_document без главы) — отдельный
  // слой оркестрации (PR B), здесь его НЕТ.
  const deleteChapter = useCallback(async (chapterId: string) => {
    setChaptersError(null);

    try {
      const { error } = await supabase
        .from('chapters')
        .delete()
        .eq('id', chapterId);

      if (error) {
        console.error('Chapter delete error:', error);
        setChaptersError({ message: 'Не удалось удалить главу', code: error.code });
        return;
      }

      // Reselect a neighbour BEFORE shrinking the list (resolve uses the pre-delete order).
      // Only re-point the selection when the active chapter was the one removed.
      const nextSelection = currentChapter?.id === chapterId
        ? resolveChapterAfterDelete(chapters, chapterId)
        : null;

      setChapters(prev => prev.filter(c => c.id !== chapterId));

      if (currentChapter?.id === chapterId) {
        setCurrentChapter(nextSelection);
        setCurrentScene(null);
      }
    } catch (err) {
      console.error('Chapter delete exception:', err);
      setChaptersError({ message: 'Произошла ошибка при удалении главы' });
    }
  }, [chapters, currentChapter]);

  // Сохранение изменений сцены
  const saveScene = useCallback(async (sceneId: string, updates: UpdateSceneInput) => {
    try {
      const { data, error } = await supabase
        .from('scenes')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sceneId)
        .select()
        .single();

      if (error) {
        console.error('Scene save error:', error);
        setScenesError({ message: 'Не удалось сохранить сцену', code: error.code });
        return;
      }

      setScenes(prev => prev.map(s => s.id === sceneId ? data : s));
      
      if (currentScene?.id === sceneId) {
        setCurrentScene(data);
      }
    } catch (err) {
      console.error('Scene save exception:', err);
      setScenesError({ message: 'Произошла ошибка при сохранении сцены' });
    }
  }, [currentScene]);

  // Загружаем главы при смене книги
  const currentBookId = currentBook?.id;
  useEffect(() => {
    if (currentBookId) {
      // loadChapters — async-загрузка из Supabase; синхронный setChaptersLoading(true)
      // внутри неё и есть намеренный эффект, подавляем точечно.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadChapters();
    } else {
      setChapters([]);
      setCurrentChapter(null);
      setScenes([]);
      setCurrentScene(null);
    }
  }, [currentBookId, loadChapters]);

  // Загружаем сцены при смене главы
  const currentChapterId = currentChapter?.id;
  useEffect(() => {
    if (currentChapterId) {
      // loadScenes — async-загрузка из Supabase (см. выше про set-state-in-effect).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadScenes(currentChapterId);
    } else {
      setScenes([]);
      setCurrentScene(null);
    }
  }, [currentChapterId, loadScenes]);

  const value = useMemo(
    () => ({
      chapters,
      scenes,
      currentChapter,
      currentScene,
      chaptersLoading,
      scenesLoading,
      chaptersError,
      scenesError,
      createChapter,
      createScene,
      selectChapter,
      selectScene,
      saveChapter,
      deleteChapter,
      saveScene,
      loadChapters,
    }),
    [
      chapters,
      scenes,
      currentChapter,
      currentScene,
      chaptersLoading,
      scenesLoading,
      chaptersError,
      scenesError,
      createChapter,
      createScene,
      selectChapter,
      selectScene,
      saveChapter,
      deleteChapter,
      saveScene,
      loadChapters,
    ]
  );

  return <ChaptersContext.Provider value={value}>{children}</ChaptersContext.Provider>;
}
